import { useEffect, useReducer, useState } from 'react';
import DateTimeParser from '../../../../../core/utils/DateTimeParser';
import { InfoMessageService, TranslationService } from '../../../../../core/services';
import ApiProvider from '../../../../../core/api/ApiProvider';
import { ResponseApiType } from '../../../../types/ResponseApiType';
import {
  Alert,
  Button,
  CheckboxField,
  DateField,
  Form,
  Loader,
  SelectField,
  Table,
  TextField,
} from '../../../../../core/components';
import { Field, FieldAttributes, Form as FormikForm, Formik } from 'formik';
import { ManualInstallmentPlanService } from './services/ManualInstallmentPlanService';
import { stateReducer } from './reducer/ManualInstallmentPlanReducer';
import { TActionType } from './types/ReducerType';
import { useMutation } from 'react-query';
import { validationSchema } from './validation/ManualInstallmentPlanValidation';
import styles from './ManualInstallmentPlan.module.scss';
import { ViewModel } from '../../types/ViewModel';
import { useParams } from 'react-router-dom';
import ManualInstallmentPlanApi from './api/ManualInstallmentPlanApi';
import moment from 'moment';

export type PropsType = {
  model: ViewModel;
  onClose: () => void;
};

type FormValues = {
  minInstallmentAmount: string;
  installmentAmount: string;
  startDate: string;
  showPlanTypes: boolean;
  planType?: string;
};

const ManualInstallmentPlan = (props: PropsType) => {
  const { caseNumber, tenantUId } = useParams() as { caseNumber: string; tenantUId: string };
  const [state, dispatch] = useReducer(stateReducer, {
    isShowingConfirmButton: false,
    isShowingInstallmentAmountError: '',
    table: {
      headers: [
        {
          columnName: 'id',
          headerName: '',
          isVisible: false,
        },
        {
          columnName: 'dueDate',
          headerName: TranslationService.translate('InstallmentPlanTableDueDate'),
          isVisible: true,
        },
        {
          columnName: 'termAmount',
          headerName: TranslationService.translate('InstallmentPlanTableTermAmount'),
          isVisible: true,
        },
        {
          columnName: 'remainingCapital',
          headerName: TranslationService.translate('InstallmentPlanTableRemainingCapital'),
          isVisible: true,
        },
        {
          columnName: 'remainingInterest',
          headerName: TranslationService.translate('InstallmentPlanTableRemainingInterest'),
          isVisible: true,
        },
        {
          columnName: 'remainingOther',
          headerName: TranslationService.translate('InstallmentPlanTableRemainingOther'),
          isVisible: true,
        },
        {
          columnName: 'remainingBalance',
          headerName: TranslationService.translate('InstallmentPlanTableRemainingBalance'),
          isVisible: true,
        },
      ],
      rows: [],
    },
    filter: {
      installmentAmount: '1',
      startDate: DateTimeParser.addDays(0),
      planType: '',
    },
  });
  const [formikFormValues, setFormikFormValues] = useState<FormValues>({} as FormValues);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [installmentError, setInstallmentError] = useState<string>('');
  const [installmentAdditionalFeeInfo, setInstallmentAdditionalFeeInfo] = useState<string>('');

  const confirmInstallmentPlan = useMutation(
    async () => {
      return await ApiProvider.post<ResponseApiType>(
        'InstallmentPlanModule/SubmitManualInstallmentPlan',
        {
          CaseNo: caseNumber,
          startDate: DateTimeParser.toDateFormat(state.filter.startDate, 'DD.MM.YYYY'),
          installment: state.filter.installmentAmount,
          sourceTenantId: tenantUId,
        }
      );
    },
    {
      onSuccess: (data) => {
        if (data.success) {
          InfoMessageService.success(
            TranslationService.translate('InstallmentPlanRequestSendSuccessfully')
          );
        } else {
          InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
        }

        props.onClose();
      },
      onError: (err) => {
        InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
      },
    }
  );

  const calculatedInstallmentPlanFn = useMutation(
    (values: FormValues) =>
      ManualInstallmentPlanApi.CalculateManualInstallmentPlan(
        caseNumber,
        parseFloat(values.installmentAmount),
        DateTimeParser.toDateFormat(values.startDate, 'MM/DD/YYYY')
      ),
    {
      onSuccess: (data, variables) => {
        if (data.success) {
          const rows = ManualInstallmentPlanService.mapDataToTableRows(
            data.data,
            props.model.caseBalance,
            props.model.isCurrencyVisible,
            props.model.currency
          );

          setInstallmentAdditionalFeeInfo(getInstallmentAdditionalFeeTranslation(rows.length));

          if (rows.length > props.model.maxInstallmentNr) {
            setInstallmentError(
              TranslationService.translate('InstallmentPlanMaxInstallments').replace(
                '{0}',
                props.model.maxInstallmentNr.toString()
              )
            );

            dispatch({
              type: TActionType.IsShowingConfirmButton,
              payload: false,
            });

            dispatch({
              type: TActionType.TableData,
              payload: {
                ...state.table,
                rows: [],
              },
            });
          } else if (rows.length < 2) {
            setInstallmentError(TranslationService.translate('InstallmentPlanMinInstallments'));
            dispatch({
              type: TActionType.IsShowingConfirmButton,
              payload: false,
            });

            dispatch({
              type: TActionType.TableData,
              payload: {
                ...state.table,
                rows: [],
              },
            });
          } else {
            dispatch({
              type: TActionType.Filter,
              payload: {
                installmentAmount: variables.installmentAmount,
                startDate: variables.startDate,
              },
            });
            dispatch({
              type: TActionType.TableData,
              payload: {
                ...state.table,
                rows,
              },
            });

            dispatch({
              type: TActionType.IsShowingConfirmButton,
              payload: true,
            });
          }
        } else {
          InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
        }
      },
      onError: () => {
        InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
      },
      onSettled: () => {
        setShowLoader(false);
      },
    }
  );

  const getInstallmentAdditionalFeeTranslation = (installmentsNumber: number) => {
    const { collectCountryCode } = props.model;

    if (collectCountryCode === 'NO' && installmentsNumber > 4) {
      return TranslationService.translateAndFillPlaceholders(
        'InstallmentAdditionalFeeSpecificInfo',
        ['4']
      );
    }
    return collectCountryCode !== 'NO'
      ? TranslationService.translate('InstallmentAdditionalFeeGeneralInfo')
      : '';
  };

  const handleCalculateButton = (values: FormValues) => {
    setShowLoader(true);
    setInstallmentError('');
    calculatedInstallmentPlanFn.mutate(values);
  };

  const handleConfirmButton = () => {
    confirmInstallmentPlan.mutate();
  };

  useEffect(() => {
    const values: FormValues = {
      minInstallmentAmount: props.model.minInstallmentAmount.toString(),
      installmentAmount: '1',
      showPlanTypes: false,
      startDate: DateTimeParser.addDays(0),
    };

    if (props.model.installmentPlanTypes.planTypes) {
      values.planType = props.model.installmentPlanTypes.planTypes[0];
    }

    setFormikFormValues({
      ...formikFormValues,
      ...values,
    });
  }, []);

  return (
    <div style={{ minHeight: `${props.model.minWindowHeight}px` }}>
      {state.isShowingInstallmentAmountError && (
        <Alert variant={Alert.Variants.Warning} className="mb-2">
          {state.isShowingInstallmentAmountError}
        </Alert>
      )}

      <div className="row">
        <div className="col-12">
          <p className={styles.labelInfo}>
            {TranslationService.translate('ManualInstallmentPlanDebtCollection')}
          </p>
        </div>
      </div>

      <div className="mb-3">
        {Object.keys(formikFormValues).length > 0 && (
          <Formik
            initialValues={formikFormValues}
            validationSchema={validationSchema()}
            onSubmit={(values) => {
              handleCalculateButton(values);
            }}
            enableReinitialize={true}
          >
            {({ handleSubmit, values, errors, touched }) => (
              <FormikForm className={styles.formWrapper}>
                <div className="row">
                  <div className="col-auto">
                    <Form.Group variant={Form.Group.Variants.Inline}>
                      <Form.Label for="installmentAmount">
                        {TranslationService.translate('InstallmentAmount')}
                      </Form.Label>

                      <Field name="installmentAmount">
                        {(fieldProps: FieldAttributes<any>) => (
                          <TextField
                            name={fieldProps.field.name}
                            value={fieldProps.field.value}
                            error={fieldProps.meta.error}
                            touched={fieldProps.meta.touched}
                            onChange={fieldProps.form.handleChange}
                          />
                        )}
                      </Field>
                    </Form.Group>
                  </div>

                  <div className="col-auto">
                    <Form.Group variant={Form.Group.Variants.Inline}>
                      <Form.Label for="startDate">
                        {TranslationService.translate('StartDate')}
                      </Form.Label>

                      <Field name="startDate">
                        {(fieldProps: FieldAttributes<any>) => {
                          return (
                            <DateField
                              disabledDays={[
                                (day) => {
                                  return day >= moment().add(30, 'day').toDate();
                                },
                                (day) => {
                                  return day <= moment().add(-1, 'day').toDate();
                                },
                              ]}
                              name={fieldProps.field.name}
                              value={fieldProps.field.value}
                              error={fieldProps.meta.error}
                              onChange={(v, e) => {
                                fieldProps.form.handleChange(e);
                              }}
                            />
                          );
                        }}
                      </Field>
                    </Form.Group>
                  </div>

                  <CheckboxField
                    checked={props.model.installmentPlanTypes.planTypes.length > 0}
                    name="showPlanTypes"
                    id="showPlanTypes"
                    isHidden={true}
                  />

                  {props.model.installmentPlanTypes.planTypes &&
                    props.model.installmentPlanTypes.planTypes.length > 0 && (
                      <div className="col-auto">
                        <Form.Group variant={Form.Group.Variants.Inline}>
                          <Form.Label for="installmentPlanTypes">
                            {TranslationService.translate('InstallmentPlanTypes')}
                          </Form.Label>

                          <div className="col-fixed-2">
                            <Field name="installmentPlanTypes">
                              {(fieldProps: FieldAttributes<any>) => {
                                return (
                                  <>
                                    <SelectField
                                      name={fieldProps.field.name}
                                      value={fieldProps.field.value}
                                      error={fieldProps.meta.error}
                                      touched={fieldProps.meta.touched}
                                      options={props.model.installmentPlanTypes.planTypes.map(
                                        (type) => ({
                                          name: type,
                                          value: type,
                                        })
                                      )}
                                      onChange={(v, e) => {
                                        fieldProps.form.handleBlur(e);
                                        fieldProps.form.handleChange(e);
                                      }}
                                    />
                                  </>
                                );
                              }}
                            </Field>
                          </div>
                        </Form.Group>
                      </div>
                    )}

                  <div className="col-auto">
                    <Button
                      type={Button.Types.Submit}
                      variant={Button.Variants.Primary}
                      size={Button.Sizes.LG}
                      id="CalculateButton"
                    >
                      {TranslationService.translate('Calculate')}
                    </Button>
                  </div>
                </div>
              </FormikForm>
            )}
          </Formik>
        )}
      </div>

      {installmentError && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50px',
            color: 'red',
          }}
        >
          <div className={styles.errormessage}>
            <span>{installmentError}</span>
          </div>
        </div>
      )}

      {showLoader && (
        <div style={{ height: '50px', position: 'relative' }}>
          <Loader />
        </div>
      )}

      {state.table.headers.length > 0 && state.table.rows.length > 0 && showLoader === false && (
        <>
          {installmentAdditionalFeeInfo && (
            <Alert variant={Alert.Variants.Info} className="mb-2">
              {installmentAdditionalFeeInfo}
            </Alert>
          )}
          <Table
            headers={state.table.headers}
            rows={state.table.rows}
            keyField={state.table.headers[0].columnName}
            paginationProps={{
              hideSizePerPage: true,
              hidePageListOnlyOnePage: true,
            }}
            noDataIndication={noDataIndication}
          />
        </>
      )}

      {state.isShowingConfirmButton && (
        <div className="mt-3">
          {confirmInstallmentPlan.isLoading && <Loader opacity={0.4} />}

          <Button
            type={Button.Types.Button}
            variant={Button.Variants.Primary}
            size={Button.Sizes.LG}
            onClick={handleConfirmButton}
          >
            {TranslationService.translate('Confirm')}
          </Button>
        </div>
      )}
    </div>
  );
};

function noDataIndication() {
  return (
    <div>
      <p>{TranslationService.translate('ManualInstallmentPlanNoDataTable')}</p>
    </div>
  );
}

export default ManualInstallmentPlan;
